import { type Hotel } from '@/config/objects/hotels';
import {
  Breadcrumbs,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  PageContainer,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Building2, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function HotelsDashboardPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Hotels', current: true },
  ];

  // Fetch hotels - backend filters by organization for non-internal users
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['Hotel', 'list'],
    queryFn: async () => {
      return apiClient.get<{ success: boolean; data: Hotel[] }>(
        '/hotels',
        {
          params: {
            limit: 250,
            orderBy: { name: 'asc' },
          },
        },
      );
    },
  });

  // Memoize allHotels to prevent dependency issues
  const allHotels = useMemo(() => data?.data || [], [data?.data]);

  // Filter hotels based on search query (browser-side filtering, sorting done by database)
  const hotels = useMemo(() => {
    if (!searchQuery.trim()) {
      return allHotels;
    }

    const query = searchQuery.toLowerCase();
    return allHotels.filter(
      hotel =>
        hotel.name.toLowerCase().includes(query) ||
        hotel.city?.toLowerCase().includes(query) ||
        hotel.state?.toLowerCase().includes(query),
    );
  }, [allHotels, searchQuery]);

  return (
    <PageContainer>
      <Breadcrumbs items={breadcrumbs} className="mb-4" />

      <div className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">Your Hotels</h1>
        <p className="text-muted-foreground mt-1">
          Manage tours, amenities, and promotional offers for each hotel
        </p>
      </div>

      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          type="text"
          placeholder="Search hotels by name, city, or state..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {error ? (
        <ErrorState
          title="Failed to load hotels"
          error={error}
          action={{
            label: 'Retry',
            onClick: () => refetch(),
          }}
        />
      ) : isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="overflow-hidden flex flex-col">
              <Skeleton className="h-48 w-full" />
              <div className="p-6 flex flex-col flex-1">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-4 flex-1" />
                <Skeleton className="h-10 w-full" />
              </div>
            </Card>
          ))}
        </div>
      ) : hotels.length === 0 ? (
        <Card>
          <CardHeader className="text-center py-12">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
            </div>
            <CardTitle>No Hotels Found</CardTitle>
            <CardDescription>
              {searchQuery.trim()
                ? `No hotels match "${searchQuery}". Try a different search term.`
                : "Your organization doesn't have any hotels yet. Please contact your administrator."}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hotels.map(hotel => (
            <Card
              key={hotel.id}
              className="h-full transition-all duration-200 hover:shadow-lg hover:border-primary/50 cursor-pointer overflow-hidden flex flex-col p-0"
            >
              {hotel.featuredImage?.url ? (
                <img
                  src={hotel.featuredImage.url}
                  alt={hotel.name}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                  <Building2 className="w-16 h-16 text-primary/30" />
                </div>
              )}
              <div className="px-6 pt-3 pb-6 flex-1 flex flex-col">
                <h3 className="text-xl font-semibold mb-2">{hotel.name}</h3>
                <p className="text-sm text-muted-foreground mb-4 flex-1">
                  {hotel.city}
                  {hotel.city && hotel.state ? ', ' : ''}
                  {hotel.state}
                </p>
                <Button onClick={() => navigate(`/hotels/${hotel.id}`)} className="w-full">
                  Manage
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
